import { Action, createReducer, on } from '@ngrx/store';
import {
    ClearGlobalStateAction,
    ClearServicesModuleAction,
    SetIsGenericUrlAction,
    SetLanguageAction,
    SetNavigationAction,
    SetNavigationMainItemsAction,
    SetNavigationSecItemsAction,
    SetOrganisationAction,
    SetOrganisationConfigurationAction,
    SetOrganisationFeaturesConfigurationAction,
    SetOrganisationsAction,
    SetServiceCategoriesAction,
    SetServiceStatusesAction,
    SetServicesAction,
    SetCollectionsAction,
    SetOrganisationAreasAction,
    ClearCollectionsModuleAction,
    SetOrganisationColourAction,
    ClearAccessModuleAction,
    SetAccessAction,
} from './global.actions';

import { GlobalState } from '../global.model';
import { initialState } from './global.state';

const reducer = createReducer(
    initialState,
    on(ClearGlobalStateAction, (_state: GlobalState) => ({ ...initialState })),
    on(SetOrganisationAction, (state: GlobalState, { organisation }) => ({
        ...state,
        organisation: {
            ...state.organisation,
            id: organisation?._id,
            tag: organisation?.tag,
            logo: organisation?.logo,
            name: organisation?.name,
            bounds: organisation?.bounds,
            subdomain: organisation?.subdomain,
            subscriptions: organisation?.subscriptions,
            taxes: organisation?.taxes,
            mapZoom: organisation?.mapZoom,
            timeZone: organisation?.timeZone,
            colour: organisation?.colour,
        },
    })),
    on(SetNavigationAction, (state: GlobalState, { navigation }) => ({
        ...state,
        navigation: {
            ...state?.navigation,
            primary: [...(navigation?.primary ?? [])],
            secondary: [...(navigation?.secondary ?? [])],
            mobileLeftNavBar: [...(navigation?.mobileLeftNavBar ?? [])],
            mobileRightNavBar: [...(navigation?.mobileRightNavBar ?? [])],
        },
    })),
    on(SetNavigationMainItemsAction, (state: GlobalState, { items }) => ({
        ...state,
        navigation: {
            ...state.navigation,
            mainItems: [...items],
        },
    })),
    on(SetIsGenericUrlAction, (state: GlobalState, { isGenericUrl }) => ({
        ...state,
        isGenericUrl,
    })),
    on(SetNavigationSecItemsAction, (state: GlobalState, { items }) => ({
        ...state,
        navigation: {
            ...state.navigation,
            secondaryItems: [...items],
        },
    })),
    on(SetLanguageAction, (state: GlobalState, { language }) => ({
        ...state,
        language,
    })),

    on(SetOrganisationsAction, (state: GlobalState, { organisations }) => ({
        ...state,
        organisations: [
            ...organisations.map((o) => ({
                id: o?._id,
                tag: o?.tag,
                name: o?.name,
                logo: o?.logo,
                subdomain: o?.subdomain,
                navigation: o?.navigation,
                subscriptions: o?.subscriptions,
                taxes: o?.taxes,
                mapZoom: o?.mapZoom,
                bcitiId: o?.bcitiId,
                bounds: o?.bounds,
                address: o?.address,
                configuredChannels: o?.configuredChannels,
                countryTag: o?.countryTag,
                entryRoute: o?.entryRoute,
                requestSearchRadius: o?.requestSearchRadius,
                ressources: o?.ressources,
                stateShortCode: o?.stateShortCode,
                timeZone: o?.timeZone,
                colour: o?.colour,
            })),
        ],
    })),
    on(SetOrganisationConfigurationAction, (state: GlobalState, { configuration }) => ({
        ...state,
        organisation: {
            ...state.organisation,
            name: configuration.name,
            lang: configuration.lang,
            configuration: {
                ...state.organisation.configuration,
                name: configuration.name,
                card: configuration.card,
                lang: configuration.lang,
                support: configuration.support,
                primaryCard: configuration.primaryCard,
                family: configuration.family,
                adminTabs: configuration.adminTabs,
                subTypes: configuration.subTypes,
                dummyAccount: configuration.dummyAccount,
                request: configuration.request,
                cardPrint: configuration.cardPrint,
                modules: configuration.modules,
                consultation: configuration.consultation,
                contactInformation: configuration?.contactInformation,
                profileOptions: configuration?.profileOptions,
                dashboardUrl: configuration?.dashboardUrl,
                residence: configuration?.residence,
            },
        },
    })),
    on(SetOrganisationFeaturesConfigurationAction, (state: GlobalState, { features }) => ({
        ...state,
        organisation: {
            ...state.organisation,
            configuration: {
                ...state.organisation.configuration,
                features,
            },
        },
    })),
    on(SetServiceCategoriesAction, (state: GlobalState, { categories }) => ({
        ...state,
        serviceCategories: categories,
    })),
    on(SetServiceStatusesAction, (state: GlobalState, { statuses }) => ({
        ...state,
        serviceStatuses: statuses,
    })),
    on(SetServicesAction, (state: GlobalState, { services }) => ({
        ...state,
        services: [...services],
    })),
    on(ClearServicesModuleAction, (state: GlobalState) => ({
        ...state,
        services: initialState.services,
        serviceCategories: initialState.serviceCategories,
        serviceStatuses: initialState.serviceStatuses,
    })),
    on(ClearCollectionsModuleAction, (state: GlobalState) => ({
        ...state,
        collections: initialState.collections,
    })),
    on(SetCollectionsAction, (state: GlobalState, { collections }) => ({
        ...state,
        collections: [...collections],
    })),
    on(SetOrganisationAreasAction, (state: GlobalState, { areas }) => ({
        ...state,
        areas: [...areas],
    })),
    on(SetOrganisationColourAction, (state: GlobalState, { colour }) => ({
        ...state,
        colour: colour,
    })),
    on(ClearAccessModuleAction, (state: GlobalState) => ({
        ...state,
        access: initialState.access,
    })),
    on(SetAccessAction, (state: GlobalState, { access }) => ({
        ...state,
        access: [...access],
    })),
);

export function GlobalReducer(state: GlobalState, action: Action): GlobalState {
    return reducer(state, action);
}
